import Icon from "@components/Icon";
import {
	closeIconSizes,
	colours,
	device,
	laptopMediaQuery,
	tabletMediaQuery,
	useDeviceDetect,
} from "@helpers";
import { useQrModalContext } from "@hooks";
import {
	IModuleAppStoreFields,
	type IModulePlanFields,
} from "@types-contentful";
import { type Asset, Entry } from "contentful";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Expandable from "./Expandable";

interface IModuleAppStoreProps extends IModulePlanFields {
	qrData: {
		sys: {
			id: string;
		};
	};
}

const Container = styled.div<{ showModal: boolean }>`
  display: ${({ showModal }) => (showModal ? "flex" : "none")};
  align-items: center;
  height: 100vh;

  @media (min-width: ${device.laptop}) {
    position: static;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colours.PEACH};
  width: 100%;
  overflow: auto;
  height: 100%;
  justify-content: space-between;
  box-shadow: 0px 4px 15px #00000029;

  ${tabletMediaQuery} {
    height: auto;
    position: relative;
    margin: 10% 8%;
    border-radius: 32px;
    overflow: none;
  }

  ${laptopMediaQuery} {
    height: auto;
    position: relative;
    margin: 10% 21.6%;
    border-radius: 32px;
    overflow: none;
  }

  @media (min-width: ${device.desktopS}) {
    height: auto;
    position: relative;
    margin: 10% 21%;
    border-radius: 32px;
    overflow: none;
  }
`;

const Heading = styled.h2`
  /* font-size: 34px;
  line-height: 1.1em;

  ${tabletMediaQuery} {
    font-size: 52px;
  }

  ${laptopMediaQuery} {
    font-size: 52px;
  }

  @media (min-width: ${device.desktopS}) {
    font-size: 72px;
  } */
`;

const Content = styled.div`
  padding: 61px 32px 41px;

  ${tabletMediaQuery} {
    padding: 80px 50px 30px;
  }

  ${laptopMediaQuery} {
    padding: 80px 92px 50px;
  }

  @media (min-width: ${device.desktopS}) {
    padding: 120px 92px 70px;
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 17px;

  ${tabletMediaQuery} {
    right: 33px;
    top: 33px;
  }

  ${laptopMediaQuery} {
    right: 33px;
    top: 33px;
  }

  @media (min-width: ${device.desktopS}) {
    right: 33px;
    top: 33px;
  }
`;

const Button = styled.div`
  :hover {
    cursor: pointer;
  }
`;

const QRContainer = styled.div`
  /* align-self: flex-end;
  width: 108px;

  ${laptopMediaQuery} {
    width: 124px;
  }

  @media (min-width: ${device.desktopS}) {
    width: 140px;
  } */
`;

const StyledAppStoreBadge = styled.div`
  width: 160px;
  margin-top: 15px;
  max-width: none;
`;

const StyledPlayStoreBadge = styled.div`
  width: 180px;
  margin-top: 20px;
  max-width: none;
`;

const DownLoadSection = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex: 1;
  background-color: ${colours.WHITE};
  padding: 41px 32px;
  text-align: center;
  flex-direction: ${({ isMobile = true }) => (isMobile ? "column" : "row")};
  justify-content: ${({ isMobile = true }) =>
		isMobile ? "flex-start" : "space-between"};
  height: 320px;

  ${tabletMediaQuery} {
    height: 178px;
    padding: 30px 50px;
  }

  ${laptopMediaQuery} {
    max-height: 40%;
    padding: 50px 90px;
  }

  @media (min-width: ${device.desktopS}) {
    max-height: 40%;
    padding: 50px 92px;
  }
`;

const H6 = styled.h6`
  text-align: center;

  /* ${laptopMediaQuery} {
    font-size: 26px;
  } */
`;

const StoreSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
`;

const LinkTag = styled(Link)`
  color: ${colours.MIDNIGHT_BLUE};

  /* ${laptopMediaQuery} {
    font-size: 16px;
  }

  @media (min-width: ${device.desktopS}) {
    font-size: 22px;
  } */
`;

const SwitchExpanable = styled(Expandable)`
  margin-top: 0px;
  padding: 0px;
  justify-content: space-between;
  min-height: 38px;

  ${tabletMediaQuery} {
    min-height: 48px;
  }

  ${laptopMediaQuery} {
    min-height: 48px;
  }

  @media (min-width: ${device.desktopS}) {
    min-height: 50px;
  }
`;

const SwitchStep = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 20px;
  flex: 1;

  ${tabletMediaQuery} {
    margin-top: 40px;
  }

  ${laptopMediaQuery} {
    margin-top: 40px;
  }
`;

const switchToFelixInfo = [
	{
		title: "1. Download the felix app",
		iconName: "mobile",
		detail:
			"Sign up to the $35/mth plan in the felix app. Don’t worry, your plan won’t actually start until you activate your SIM, so you won’t lose any days.",
	},
	{
		title: "2. Order your SIM with free express delivery",
		iconName: "mail",
		detail:
			"We'll deliver your SIM with free express delivery, so it should arrive in 1-3 business days. Along with a little surprise!",
	},
	{
		title: "3. Activate your SIM in the app",
		iconName: "sim-valid",
		detail: (
			<>
				Scan your SIM in the felix app whenever you&apos;re ready. You can bring
				your number over or get a new one!{" "}
				<a href="https://help.felixmobile.com.au/support/solutions/articles/51000178670">
					How to bring your number over to felix
				</a>
			</>
		),
	},
];

const IconContainer = styled.div`
  width: 38px;
  height: 38px;
  background-color: ${colours.WHITE};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  align-self: flex-start;

  ${tabletMediaQuery} {
    width: 48px;
    height: 48px;
    margin-right: 21px;
  }

  ${laptopMediaQuery} {
    width: 48px;
    height: 48px;
    margin-right: 31px;
  }

  @media (min-width: ${device.desktopS}) {
    width: 50px;
    height: 50px;
    margin-right: 30px;
  }
`;

const ExpandableSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SwitchDetail = styled.p`
  font-family: EuclidCircularALight;
  /* font-size: 14px;

  ${tabletMediaQuery} {
    font-size: 18px;
  }

  ${laptopMediaQuery} {
    font-size: 18px;
  }

  @media (min-width: ${device.desktopS}) {
    font-size: 22px;
  } */
`;

const P = styled.p`
  text-align: left;
  /* font-size: 18px; */

  @media (min-width: ${device.desktopS}) {
    /* font-size: 26px; */
  }
`;

const DesktopTextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const iconSizes = {
	iconMobile: 25,
	iconTablet: 35,
	iconLaptop: 35,
	iconDesktop: 40,
	iconDesktopLarge: 40,
};

const QRModal = ({ qrData, ctaButtonQrModal }: IModuleAppStoreProps) => {
	const [qrCodeData, setQrCodeData] = useState<Asset | undefined>();
	const [QRCode, setQRCode] = useState<Asset | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Mock of an asynchronous data fetch
		const fetchData = async () => {
			try {
				setLoading(true);

				// This is just a mock. Replace this with actual data fetching logic.
				const data = await new Promise((resolve) =>
					setTimeout(() => resolve(ctaButtonQrModal?.fields?.qrCode), 1000),
				);
				// @ts-ignore
				setQRCode(data);
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [ctaButtonQrModal]);

	// Now, you can access properties like:
	const androidURL = ctaButtonQrModal?.fields?.androidUrl;
	const appleURL = ctaButtonQrModal?.fields?.appleUrl;
	// @ts-ignore
	const id = qrData?.sys?.id?.sys?.id;

	const { isMobile, isIOS, isAndroid } = useDeviceDetect();

	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);

	// @ts-ignore
	const { qrModalId, remvoeQrModalContext: closeModal } = useQrModalContext();

	const renderLinkTag = () => (
		<Link href="/buy">or continue in your browser</Link>
	);
	// if (loading) {
	//   return <div>Loading...</div>;
	// }
	const renderDownloadSection = () => {
		if (isMobile) {
			return (
				// @ts-ignore
				<DownLoadSection>
					<H6>Download the app to get started!</H6>

					<StoreSection>
						{!isAndroid && appleURL && (
							<StyledAppStoreBadge>
								<Image
									alt="Get it on Appstore"
									src="/images/AppStoreBadge.png"
									width={162.5}
									height={48}
									data-gtm-cta-text="appleUrl"
									data-gtm-cta-position="QrModal"
								/>
							</StyledAppStoreBadge>
						)}
						{/* @ts-ignore */}
						{!isIOS && androidURL && <StyledPlayStoreBadge link={androidURL} />}
					</StoreSection>
					{renderLinkTag()}
				</DownLoadSection>
			);
		}

		return (
			<DownLoadSection isMobile={isMobile}>
				<DesktopTextSection>
					<div>
						<H6>Download the app to get started!</H6>
						<P>Scan QR code to download</P>
					</div>
					{renderLinkTag()}
				</DesktopTextSection>
				{`${QRCode?.fields?.file?.url}`.length > 1 && (
					<QRContainer>
						<img
							src={`https:${QRCode?.fields?.file?.url}`}
							/* @ts-ignore */
							objectFit="cover"
							loading="lazy"
							alt="Qr code"
						/>
					</QRContainer>
				)}
			</DownLoadSection>
		);
	};

	return (
		<Container
			className="full-size-modal"
			showModal={qrModalId.sys.id === id}
			onClick={() => closeModal()}
		>
			<ContentSection
				onClick={(e) => {
					e.stopPropagation();
				}}
			>
				<ButtonContainer>
					<Button onClick={closeModal}>
						<Icon iconName="close" iconSizes={closeIconSizes} alt="close" />
					</Button>
				</ButtonContainer>
				<Content>
					<Heading>How to switch to felix</Heading>
					{switchToFelixInfo.map((info, index) => (
						<SwitchStep key={index}>
							<IconContainer>
								<Icon
									iconName={info.iconName}
									iconSizes={iconSizes}
									alt={info.iconName}
								/>
							</IconContainer>

							<ExpandableSection>
								<SwitchExpanable
									key={info.title}
									renderDetail={() => (
										<SwitchDetail>{info.detail}</SwitchDetail>
									)}
									title={info.title}
								/>
							</ExpandableSection>
						</SwitchStep>
					))}
				</Content>
				{renderDownloadSection()}
			</ContentSection>
		</Container>
	);
};

export default QRModal;
